// MIT License - Copyright (c) 2026 Doug McNall
import { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const AccessControlContext = createContext(null);

/**
 * Hook for managing user access control and permissions
 * Integrates with the gateway-backed access control system
 * 
 * Permission Model:
 * - Super Admins: Can see and do everything across all tabs/groups
 * - Each tab (Products, Orders, Customers, etc.) is a group
 * - Users can be added to any tab/group with either:
 *   - Viewer role: Read-only access to that specific tab
 *   - Group Admin role: Can control other people's roles in that specific tab/group
 * - Multi-group membership: Users can have different roles across different tabs
 * - Group Admin powers: Group admins of a tab can manage other users' roles within that specific tab
 */
export const useAccessControl = () => {
  const ctx = useContext(AccessControlContext);
  // Always call the hook (rules of hooks), but only use its value if no provider
  const standalone = useAccessControlInternal();
  return ctx ?? standalone;
};

// Provider — mount once at app level so all consumers share one load
export const AccessControlProvider = ({ children }) => {
  const value = useAccessControlInternal();
  return (
    <AccessControlContext.Provider value={value}>
      {children}
    </AccessControlContext.Provider>
  );
};

function useAccessControlInternal() {
  const { user, isAuthenticated } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [userGroupAssignments, setUserGroupAssignments] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [tabPermissions, setTabPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasAttemptedLoadRef = useRef(false);
  const currentUserEmailRef = useRef(null);

  // Load user access control data
  const loadAccessControl = useCallback(async () => {
    if (!isAuthenticated || !user?.attributes?.email) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Use email address for profile lookup
      const userEmail = user.attributes.email;

      // Add timeout to API calls to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('API call timeout after 15 seconds')), 15000);
      });

      try {
        // Get user profile from admin_users table with timeout
        const profileResponse = await Promise.race([
          api.accessControl.getUserProfile(userEmail),
          timeoutPromise
        ]);
        
        if (!profileResponse.success) {
          throw new Error(`getUserProfile failed: ${profileResponse.error || 'Unknown error'}`);
        }
        
        const profile = profileResponse.data;
        setUserProfile(profile);

        // Load all groups (which are the admin tabs) for reference
        const tabGroups = [
          { id: 'dashboard', name: 'Dashboard', description: 'Overview and analytics dashboard' },
          { id: 'products', name: 'Products', description: 'Product catalog management' },
          { id: 'categories', name: 'Categories', description: 'Product category management' },
          { id: 'orders', name: 'Orders', description: 'Order management and tracking' },
          { id: 'customers', name: 'Customers', description: 'Customer information management' },
          { id: 'coupons', name: 'Coupons', description: 'Discount code management' },
          { id: 'users', name: 'Users', description: 'User account and permissions management' },
          { id: 'recipes', name: 'Recipes', description: 'Recipe content management' },
          { id: 'reviews', name: 'Reviews', description: 'Product review management' },
          { id: 'manufacturing', name: 'Manufacturing', description: 'Production batch tracking' },
          { id: 'shipping', name: 'Shipping', description: 'Shipping configuration and tracking' },
          { id: 'payment-gateways', name: 'Payment Gateways', description: 'Payment gateway setup and management' },
          { id: 'revenue', name: 'Revenue', description: 'Revenue analytics and reporting' },
          { id: 'system', name: 'System', description: 'System administration and audit logs (Supervisor only)' }
        ];
        setAllGroups(tabGroups);

        if (profile) {
          // Check if user is super admin
          const isSuperAdmin = profile.user_group_id === 'superuser' || profile.is_super_admin === true;
          
          if (isSuperAdmin) {
            // Super admins get all permissions for all tabs
            const superAdminPermissions = {};
            tabGroups.forEach(tab => {
              superAdminPermissions[tab.id] = {
                canRead: true,
                canWrite: true,
                canDelete: true,
                canAdmin: true
              };
            });
            setTabPermissions(superAdminPermissions);
            setUserGroupAssignments([{
              id: 'super_admin_assignment',
              user_email: userEmail,
              tab_group_id: 'superuser',
              role: 'super_admin',
              assigned_at: new Date().toISOString(),
              assigned_by: 'system'
            }]);
          } else {
            // Load user's tab group assignments (which tabs they have access to and their role in each)
            const assignmentsResponse = await Promise.race([
              api.accessControl.getUserTabGroupAssignments(userEmail),
              timeoutPromise
            ]);
            
            if (!assignmentsResponse.success) {
              throw new Error(`getUserTabGroupAssignments failed: ${assignmentsResponse.error || 'Unknown error'}`);
            }
            
            const assignments = assignmentsResponse.data || [];
            setUserGroupAssignments(Array.isArray(assignments) ? assignments : []);

            const resolved = resolveTabPermissions(profile, assignments);
            setTabPermissions(resolved || {});
          }
        } else {
          // No profile returned by the gateway — user may not be set up yet
          setUserProfile(null);
          setError('User profile not found. Please contact administrator.');
        }
      } catch (apiError) {
        console.error('useAccessControl: API call failed:', apiError);
        setError(`Failed to load user permissions: ${apiError.message}`);
        setUserProfile(null);
      }
    } catch (err) {
      console.error('useAccessControl: Failed to load access control data:', err);
      setError(`Access control error: ${err.message}`);
      setUserProfile(null);
    } finally {
      // Always set loading to false to prevent infinite loading
      setLoading(false);
    }
  }, [isAuthenticated, user?.attributes?.email]); // Only depend on email, not the entire attributes object

  // Load access control data when user changes
  useEffect(() => {
    const userEmail = user?.attributes?.email;
    
    // Reset if user changed
    if (currentUserEmailRef.current !== userEmail) {
      hasAttemptedLoadRef.current = false;
      currentUserEmailRef.current = userEmail;
      setUserProfile(null);
      setUserGroupAssignments([]);
      setAllGroups([]);
      setTabPermissions({});
      setError(null);
    }
    
    // Only load if we haven't attempted for this user yet
    if (isAuthenticated && userEmail && !hasAttemptedLoadRef.current) {
      hasAttemptedLoadRef.current = true;
      
      // Add timeout protection to prevent infinite loading
      const timeoutId = setTimeout(() => {
        console.warn('Access control loading timed out, forcing completion');
        setLoading(false);
        setUserProfile(null);
        setError('Access control loading timed out');
      }, 20000); // 20 second timeout
      
      loadAccessControl().finally(() => {
        clearTimeout(timeoutId);
      });
    } else if (!isAuthenticated || !userEmail) {
      setLoading(false);
    }
  }, [isAuthenticated, user?.attributes?.email, loadAccessControl]);

  // Check if user has access to a specific tab
  const hasTabAccess = useCallback((tabId) => {
    if (!userProfile || !tabPermissions) return false;
    
    // Super users have access to everything
    if (isSuperUser()) return true;
    
    // Check specific tab permissions
    return tabPermissions[tabId]?.canRead || false;
  }, [userProfile, tabPermissions]);

  // Check if user can perform specific action on a tab
  const canPerformAction = useCallback((tabId, action) => {
    if (!userProfile || !tabPermissions) return false;
    
    // Super users can do everything
    if (isSuperUser()) return true;
    
    const tabPerms = tabPermissions[tabId];
    if (!tabPerms) return false;

    switch (action) {
      case 'read':
        return tabPerms.canRead;
      case 'write':
      case 'create':
      case 'update':
        return tabPerms.canWrite;
      case 'delete':
        return tabPerms.canDelete;
      case 'admin':
        return tabPerms.canAdmin;
      default:
        return false;
    }
  }, [userProfile, tabPermissions]);

  // Check if user has Super User access
  const hasSuperUserAccess = useCallback(() => {
    return isSuperUser();
  }, []);

  // Check if user is super user
  const isSuperUser = useCallback(() => {
    return userProfile?.user_group_id === 'superuser' || userProfile?.is_super_admin === true;
  }, [userProfile]);

  // Check if user can create new users (super admins and group admins)
  const canCreateUsers = useCallback(() => {
    if (isSuperUser()) return true;
    
    // Group admins can create users in their groups
    return userGroupAssignments.some(assignment => assignment.role === 'group_admin');
  }, [userGroupAssignments]);

  // Check if user can admin a specific tab/group
  const canAdminGroup = useCallback((tabId) => {
    // Super users can admin all tabs
    if (isSuperUser()) return true;
    
    // Group admins can admin their assigned tabs
    return userGroupAssignments.some(assignment => 
      assignment.tab_group_id === tabId && assignment.role === 'group_admin'
    );
  }, [userGroupAssignments]);

  // Check if user can view/manage a specific user
  const canManageUser = useCallback((targetUser) => {
    // Super users can manage all users
    if (isSuperUser()) return true;
    
    // Group admins can manage users who have access to their tabs
    const adminTabs = userGroupAssignments
      .filter(assignment => assignment.role === 'group_admin')
      .map(assignment => assignment.tab_group_id);
    
    if (adminTabs.length === 0) return false;
    
    // Check if target user has assignments in any of the admin's tabs
    const targetUserTabs = Array.isArray(targetUser.tab_assignments) 
      ? targetUser.tab_assignments.map(assignment => assignment.tab_group_id)
      : []; // No fallback needed since this is the new model
    
    return adminTabs.some(adminTab => targetUserTabs.includes(adminTab));
  }, [userGroupAssignments]);

  // Get list of tabs the current user can admin
  const getAdminableGroups = useCallback(() => {
    if (isSuperUser()) {
      // Super users can admin all tabs
      return (Array.isArray(allGroups) ? allGroups : []).map(group => group.id);
    }
    
    // Group admins can admin their assigned tabs
    return userGroupAssignments
      .filter(assignment => assignment.role === 'group_admin')
      .map(assignment => assignment.tab_group_id);
  }, [userGroupAssignments, allGroups]);

  // Get list of accessible tabs for current user
  const getAccessibleTabs = useCallback(() => {
    // Return empty array if access control is still loading
    if (loading) {
      return [];
    }
    
    // Super users get all tabs except explicitly excluded
    if (isSuperUser()) {
      const allTabs = [
        'dashboard', 'orders', 'customers', 'products', 'categories',
        'recipes', 'reviews', 'revenue', 'manufacturing', 'shipping',
        'payment-gateways', 'coupons', 'promotions', 'media-library', 'support-tickets', 'recall-lookup', 'users', 'system', 'api-test'
      ];
      const excludedTabs = []; // Add tab IDs here to restrict from superuser
      return allTabs.filter(tabId => !excludedTabs.includes(tabId));
    }
    
    // Return tabs user has read access to based on group permissions
    return Object.keys(tabPermissions).filter(tabId => 
      tabPermissions[tabId]?.canRead
    );
  }, [loading, tabPermissions]);

  // Update user's last login timestamp
  const updateLastLogin = useCallback(async () => {
    if (!userProfile?.email) return;
    
    try {
      await api.accessControl.updateLastLogin(userProfile.email);
    } catch (err) {
      console.error('Failed to update last login:', err);
    }
  }, [userProfile]);

  // Create new admin user
  const createAdminUser = useCallback(async (userData) => {
    try {
      const newUser = await api.accessControl.createAdminUser(userData);
      return { success: true, user: newUser };
    } catch (err) {
      console.error('Failed to create admin user:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // Add user to tab/group with specific role
  const addUserToGroup = useCallback(async (userEmail, tabId, role) => {
    try {
      await api.accessControl.addUserToTabGroup(userEmail, tabId, role);
      
      // Reload access control if updating current user
      if (userEmail === user?.attributes?.email) {
        await loadAccessControl();
      }
      
      return { success: true };
    } catch (err) {
      console.error('Failed to add user to tab group:', err);
      return { success: false, error: err.message };
    }
  }, [user?.attributes?.email, loadAccessControl]);

  // Remove user from tab/group
  const removeUserFromGroup = useCallback(async (userEmail, tabId) => {
    try {
      await api.accessControl.removeUserFromTabGroup(userEmail, tabId);
      
      // Reload access control if updating current user
      if (userEmail === user?.attributes?.email) {
        await loadAccessControl();
      }
      
      return { success: true };
    } catch (err) {
      console.error('Failed to remove user from tab group:', err);
      return { success: false, error: err.message };
    }
  }, [user?.attributes?.email, loadAccessControl]);

  // Update user's role in a tab/group
  const updateUserRoleInGroup = useCallback(async (userEmail, tabId, newRole) => {
    try {
      await api.accessControl.updateUserRoleInTabGroup(userEmail, tabId, newRole);
      
      // Reload access control if updating current user
      if (userEmail === user?.attributes?.email) {
        await loadAccessControl();
      }
      
      return { success: true };
    } catch (err) {
      console.error('Failed to update user role in tab group:', err);
      return { success: false, error: err.message };
    }
  }, [user?.attributes?.email, loadAccessControl]);

  return {
    // State
    userProfile,
    userGroupAssignments,
    allGroups,
    tabPermissions,
    loading,
    error,
    
    // Permission checks
    hasTabAccess,
    canPerformAction,
    getAccessibleTabs,
    isSuperUser,
    hasSuperUserAccess,
    canCreateUsers,
    canAdminGroup,
    canManageUser,
    getAdminableGroups,
    
    // Actions
    updateLastLogin,
    createAdminUser,
    addUserToGroup,
    removeUserFromGroup,
    updateUserRoleInGroup,
    refreshAccessControl: () => {
      hasAttemptedLoadRef.current = false;
      return loadAccessControl();
    }
  };
};

// Pure function — resolves tab permissions from profile + assignments.
// Exported for testing.
export const resolveTabPermissions = (profile, assignments = []) => {
  if (!profile) return {};

  // Superuser gets everything
  if (profile.user_group_id === 'superuser' || profile.is_super_admin) {
    return null; // caller should grant all — null signals superuser
  }

  const tabPermissions = {};

  if (profile.functional_role?.tab_permissions) {
    // Layer 1: functional role
    Object.entries(profile.functional_role.tab_permissions).forEach(([tabId, perms]) => {
      tabPermissions[tabId] = { ...perms };
    });
  } else {
    // Layer 1 fallback: primary group defaults
    const roleDefaults = {
      admin:   { tabs: ['dashboard','orders','customers','support-tickets','products','categories','coupons','promotions','media-library','recipes','reviews','revenue','manufacturing','shipping','payment-gateways','recall-lookup','users'], perms: { canRead: true, canWrite: true, canDelete: true, canAdmin: false } },
      manager: { tabs: ['dashboard','orders','customers','products','categories','recipes','reviews'], perms: { canRead: true, canWrite: true, canDelete: false, canAdmin: false } }
    };
    const defaults = roleDefaults[profile.user_group_id];
    if (defaults) {
      defaults.tabs.forEach(tabId => { tabPermissions[tabId] = { ...defaults.perms }; });
    }
  }

  // Layer 2: individual tab assignments override
  assignments.forEach(({ tab_group_id: tabId, role }) => {
    if (role === 'group_admin') {
      tabPermissions[tabId] = { canRead: true, canWrite: true, canDelete: true, canAdmin: true };
    } else if (role === 'viewer') {
      tabPermissions[tabId] = { canRead: true, canWrite: false, canDelete: false, canAdmin: false };
    } else if (role === 'none') {
      delete tabPermissions[tabId];
    }
  });

  return tabPermissions;
};

// Helper functions for common permission patterns
export const accessControlHelpers = {
  // Check if user has Super User access
  hasSuperUserAccess: (userProfile) => {
    return userProfile?.user_group_id === 'superuser' || userProfile?.is_super_admin === true;
  },
  
  // Check if user can create new users (super admins and tab admins)
  canCreateUsers: (userProfile, userGroupAssignments) => {
    if (accessControlHelpers.hasSuperUserAccess(userProfile)) return true;
    
    // Tab admins can create users
    return Array.isArray(userGroupAssignments) && 
           userGroupAssignments.some(assignment => assignment.role === 'group_admin');
  },
  
  // Check if user can admin users in a specific tab
  canAdminUsersInTab: (userProfile, userGroupAssignments, targetTabId) => {
    // Super users can admin all tabs
    if (accessControlHelpers.hasSuperUserAccess(userProfile)) return true;
    
    // Tab admins can admin their assigned tabs
    return Array.isArray(userGroupAssignments) && 
           userGroupAssignments.some(assignment => 
             assignment.tab_group_id === targetTabId && assignment.role === 'group_admin'
           );
  },
  
  // Check if user can access financial data
  canAccessFinancials: (tabPermissions) => {
    return tabPermissions?.revenue?.canRead || tabPermissions?.orders?.canRead;
  },
  
  // Check if user can modify product catalog
  canModifyProducts: (tabPermissions) => {
    return tabPermissions?.products?.canWrite || tabPermissions?.categories?.canWrite;
  },
  
  // Get user role display name
  getRoleDisplayName: (userProfile, userGroupAssignments) => {
    if (accessControlHelpers.hasSuperUserAccess(userProfile)) {
      return 'Super Administrator';
    }
    
    if (!Array.isArray(userGroupAssignments) || userGroupAssignments.length === 0) {
      return 'No Access';
    }
    
    const adminTabs = userGroupAssignments.filter(a => a.role === 'group_admin').length;
    const viewerTabs = userGroupAssignments.filter(a => a.role === 'viewer').length;
    
    const parts = [];
    if (adminTabs > 0) parts.push(`Admin of ${adminTabs} tab(s)`);
    if (viewerTabs > 0) parts.push(`Viewer of ${viewerTabs} tab(s)`);
    
    return parts.join(', ') || 'Unknown Role';
  },
  
  // Filter users based on what current user can manage
  filterManageableUsers: (allUsers, userProfile, userGroupAssignments) => {
    // Super users can manage all users
    if (accessControlHelpers.hasSuperUserAccess(userProfile)) {
      return allUsers;
    }
    
    // Tab admins can manage users in their assigned tabs
    const adminTabs = userGroupAssignments
      .filter(assignment => assignment.role === 'group_admin')
      .map(assignment => assignment.tab_group_id);
    
    if (adminTabs.length === 0) return [];
    
    return allUsers.filter(user => {
      // Check if target user has assignments in any of the admin's tabs
      const userTabs = Array.isArray(user.tab_assignments) 
        ? user.tab_assignments.map(assignment => assignment.tab_group_id)
        : [];
      
      return adminTabs.some(adminTab => userTabs.includes(adminTab));
    });
  },
  
  // Filter tabs based on what current user can admin
  filterAdminableTabs: (allTabs, userProfile, userGroupAssignments) => {
    // Super users can admin all tabs
    if (accessControlHelpers.hasSuperUserAccess(userProfile)) {
      return allTabs;
    }
    
    // Tab admins can admin their assigned tabs
    const adminTabs = userGroupAssignments
      .filter(assignment => assignment.role === 'group_admin')
      .map(assignment => assignment.tab_group_id);
    
    return allTabs.filter(tab => adminTabs.includes(tab.id));
  },
  
  // Check permission level for a user (for hierarchy checks)
  getPermissionLevel: (userProfile, userGroupAssignments) => {
    if (accessControlHelpers.hasSuperUserAccess(userProfile)) {
      return 4; // Super User access
    }
    
    if (!Array.isArray(userGroupAssignments) || userGroupAssignments.length === 0) {
      return 0; // No access
    }
    
    // Check highest role
    const hasAdminRole = userGroupAssignments.some(a => a.role === 'group_admin');
    const hasViewerRole = userGroupAssignments.some(a => a.role === 'viewer');
    
    if (hasAdminRole) return 3; // Tab admin
    if (hasViewerRole) return 1; // Viewer
    
    return 0; // No access
  },
  
  // Check if user can perform action based on hierarchy
  canPerformHierarchicalAction: (currentUserProfile, currentUserAssignments, targetUserProfile, targetUserAssignments, action) => {
    const currentLevel = accessControlHelpers.getPermissionLevel(currentUserProfile, currentUserAssignments);
    const targetLevel = accessControlHelpers.getPermissionLevel(targetUserProfile, targetUserAssignments);
    
    // Super users can do anything
    if (accessControlHelpers.hasSuperUserAccess(currentUserProfile)) return true;
    
    // Users can only manage users at their level or below
    if (action === 'manage' || action === 'edit') {
      return currentLevel > targetLevel;
    }
    
    // Users can only view users at their level or below
    if (action === 'view') {
      return currentLevel >= targetLevel;
    }
    
    return false;
  }
};

export default useAccessControl;